import { createWorker } from 'tesseract.js';

// ── PDF → high-res canvas image ────────────────────────────────────────────
async function pdfPageToDataUrl(file, pageNum = 1) {
  const pdfjsLib = await import('pdfjs-dist/build/pdf');
  pdfjsLib.GlobalWorkerOptions.workerSrc =
    `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

  const arrayBuffer = await file.arrayBuffer();
  const pdf         = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const numPages    = pdf.numPages;
  const images      = [];

  const pagesToRender = Math.min(numPages, 4); // up to 4 pages
  for (let p = 1; p <= pagesToRender; p++) {
    const page     = await pdf.getPage(p);
    const viewport = page.getViewport({ scale: 2.5 });
    const canvas   = document.createElement('canvas');
    canvas.width   = viewport.width;
    canvas.height  = viewport.height;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
    images.push(canvas.toDataURL('image/png'));
  }
  return { images, numPages };
}

// ── Run OCR across one or multiple images ──────────────────────────────────
export async function runOCR(file, onProgress) {
  let imageSources = [];
  let numPages     = 1;

  onProgress?.(5, 'Loading file…');

  if (file.type === 'application/pdf') {
    onProgress?.(10, 'Rendering PDF pages…');
    const result = await pdfPageToDataUrl(file);
    imageSources = result.images;
    numPages     = result.numPages;
  } else {
    imageSources = [file];
  }

  onProgress?.(20, `Starting OCR (${imageSources.length} page${imageSources.length > 1 ? 's' : ''})…`);

  const worker = await createWorker('eng', 1, {
    logger: (m) => {
      if (m.status === 'recognizing text') {
        onProgress?.(20 + Math.round(m.progress * 65), `Scanning… ${Math.round(m.progress * 100)}%`);
      }
    },
  });

  let fullText = '';
  for (let i = 0; i < imageSources.length; i++) {
    onProgress?.(20 + Math.round(((i) / imageSources.length) * 65), `OCR page ${i + 1} of ${imageSources.length}…`);
    const { data: { text } } = await worker.recognize(imageSources[i]);
    fullText += (i > 0 ? '\n' : '') + text;
  }

  await worker.terminate();
  onProgress?.(90, 'Parsing invoice data…');
  return fullText;
}

// ══════════════════════════════════════════════════════════════════════════
// INVOICE PARSER — supports AGC buy invoices AND Aureus POS sales invoices
// ══════════════════════════════════════════════════════════════════════════
export function parseInvoiceText(rawText) {
  const lines  = rawText.split('\n').map(l => l.trim()).filter(Boolean);
  const result = {
    invoiceNumber: '',
    vendor:        '',
    date:          '',
    direction:     'Purchase',
    channel:       'Retail',
    paymentMethod: '',
    notes:         '',
    lineItems:     [],
    rawText,
  };

  // ── Detect invoice type ─────────────────────────────────────────────────
  const fullText    = rawText.toUpperCase();
  const isAureus    = /SALES\s+INVOICE|SO#\d+|AUREUS\.COM|ATLANTAGOLDANDCOIN\.COM/.test(fullText);

  if (isAureus) {
    return parseAureusInvoice(lines, result, rawText);
  } else {
    return parseAGCBuyInvoice(lines, result);
  }
}

// ══════════════════════════════════════════════════════════════════════════
// AUREUS POS SALES INVOICE PARSER
// Format: Sales Invoice header, SO#XXXXX, client name top-right,
//         DD-Mon-YYYY date, Qty Ordered / Qty Delivered / Unit Price / Amount columns
//         Comments column between description and qty
//         Payment info at bottom, Processed by at bottom
// ══════════════════════════════════════════════════════════════════════════
function parseAureusInvoice(lines, result, rawText) {
  result.direction = 'Sale';
  result.channel   = 'Retail';

  // ── Invoice number: SO#17832 ────────────────────────────────────────────
  for (const line of lines) {
    const m = line.match(/SO#?(\d{4,8})/i);
    if (m) { result.invoiceNumber = 'SO' + m[1]; break; }
  }

  // ── Client name: appears after SO# line or as prominent name near top ──
  // Aureus puts client name top-right, OCR often places it after the date/SO# block
  let foundSO = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (/SO#?\d+/i.test(line)) { foundSO = true; continue; }
    if (foundSO) {
      // Skip known non-name lines
      if (/@aureus|@gmail|\.com|phone|fax|address|\d{5}|suite|#\d|invoice|sales|atlanta|gold/i.test(line)) continue;
      // Skip lines that look like emails
      if (line.includes('@')) continue;
      // Skip lines that are mostly digits (dates, phone numbers)
      if (/^\d[\d\-\/\s]+$/.test(line)) continue;
      // Must look like a name: 2+ words, or single proper-cased word
      if (line.length > 2 && line.length < 60 && /^[A-Z]/.test(line)) {
        result.vendor = line.trim();
        break;
      }
    }
  }

  // ── Date: "13-Feb-2026" or "13-Feb-2026 15:55" ─────────────────────────
  for (const line of lines) {
    // Aureus date format: DD-Mon-YYYY
    const m = line.match(/(\d{1,2})-([A-Za-z]{3})-(\d{4})/);
    if (m) {
      const parsed = new Date(`${m[2]} ${m[1]}, ${m[3]}`);
      if (!isNaN(parsed)) {
        result.date = parsed.toISOString().split('T')[0];
        break;
      }
    }
  }
  // Fallback: any recognizable date
  if (!result.date) {
    for (const line of lines) {
      const m = line.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
      if (m) {
        const parsed = new Date(m[0]);
        if (!isNaN(parsed) && parsed.getFullYear() > 2000) {
          result.date = parsed.toISOString().split('T')[0]; break;
        }
      }
    }
  }

  // ── Payment method: "Cash: $22,140 (Feb 18)" / "Zelle: $3,500 ..." ────
  const paymentLine = lines.find(l => /payments\s+received/i.test(l));
  if (paymentLine) {
    // Look at the next line(s) for payment details
    const idx = lines.indexOf(paymentLine);
    for (let i = idx + 1; i < Math.min(idx + 5, lines.length); i++) {
      const pm = lines[i].match(/^(Cash|Zelle|Venmo|Wire|Check|Credit\s*Card|Crypto|ACH)[\s:]/i);
      if (pm) {
        result.paymentMethod = pm[1].trim();
        // If multiple payment methods on same invoice, note them all
        const allPayments = [];
        for (let j = idx + 1; j < Math.min(idx + 6, lines.length); j++) {
          const pMatch = lines[j].match(/^(Cash|Zelle|Venmo|Wire|Check|Credit\s*Card|Crypto|ACH)\s*[:$]/i);
          if (pMatch) allPayments.push(lines[j].trim());
          else if (allPayments.length > 0 && /^\s*$/.test(lines[j])) break;
        }
        if (allPayments.length > 1) {
          result.paymentMethod = 'Multiple';
          result.notes = (result.notes ? result.notes + ' | ' : '') + 'Payments: ' + allPayments.join('; ');
        }
        break;
      }
    }
  }
  // Also scan inline payment lines anywhere
  if (!result.paymentMethod) {
    for (const line of lines) {
      const pm = line.match(/^(Cash|Zelle|Venmo|Wire|Check|Credit\s*Card):\s*\$/i);
      if (pm) { result.paymentMethod = pm[1]; break; }
    }
  }

  // ── Processed by → Notes ────────────────────────────────────────────────
  for (const line of lines) {
    const m = line.match(/processed\s+by:?\s+(.+)/i);
    if (m) {
      const staff = m[1].trim();
      result.notes = (result.notes ? result.notes + ' | ' : '') + 'Staff: ' + staff;
      break;
    }
  }

  // ── Line items ──────────────────────────────────────────────────────────
  // Aureus format columns: # | Description | Comments | Qty Ordered | Qty Delivered | Unit Price | Amount
  // We need to find the item rows between "ITEMS SOLD" / column header and "Subtotals:"
  let inItems = false;
  const itemLines = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Start after the column header row
    if (/^\s*#\s+description/i.test(line) || /description\s+comments\s+qty/i.test(line)) {
      inItems = true; continue;
    }
    // Stop at subtotals/totals
    if (/^subtotals?:/i.test(line) || /^grand\s+total/i.test(line)) { inItems = false; continue; }
    if (!inItems) continue;
    // Skip pure header continuations
    if (/^(ordered|delivered|qty|amount|unit\s*price|comments)$/i.test(line)) continue;
    itemLines.push(line);
  }

  result.lineItems = parseAureusItemLines(itemLines);

  return result;
}

// Parse Aureus line item rows
// Each item row starts with a row number (1, 2, 3…) followed by description, then numbers
function parseAureusItemLines(lines) {
  const items = [];
  let pending = null;

  for (const line of lines) {
    // Row starts with a line number: "1 Any Year - 1oz American Gold Buffalo"
    const rowMatch = line.match(/^(\d{1,3})\s+(.+)/);

    // Extract all dollar amounts from the line
    const amounts = [];
    const amtPat  = /\$\s*([\d]{1,3}(?:,\d{3})*(?:\.\d{2})?)/g;
    let m;
    while ((m = amtPat.exec(line)) !== null) {
      amounts.push(parseFloat(m[1].replace(/,/g, '')));
    }

    // Extract standalone numbers (qty ordered / qty delivered)
    // These appear as plain integers after the description
    const plainNums = [];
    const numPat = /(?<!\$)\b(\d{1,5}(?:\.\d{2})?)\b/g;
    const stripped = line.replace(/\$[\d,]+\.?\d*/g, ''); // remove dollar amounts first
    while ((m = numPat.exec(stripped)) !== null) {
      const n = parseFloat(m[1]);
      if (n > 0) plainNums.push(n);
    }

    if (rowMatch) {
      // Flush previous
      if (pending) items.push(finalizeAureus(pending));

      let description = rowMatch[2];

      // Strip the comment if it's a single word appended: "2021 10 oz Royal Mint Silver Valiant Coin Bar"
      // Aureus comments column often gets OCR'd inline — we'll handle this in finalize

      // The last two dollar amounts are unit price and line total
      let unitPrice   = 0;
      let totalAmount = 0;
      if (amounts.length >= 2) {
        unitPrice   = amounts[amounts.length - 2];
        totalAmount = amounts[amounts.length - 1];
      } else if (amounts.length === 1) {
        totalAmount = amounts[0];
        unitPrice   = amounts[0]; // single-unit
      }

      // Qty: Aureus shows "Qty Ordered" and "Qty Delivered" — both the same for delivered orders
      // They appear as plain integers near the end of the description text
      // Strip dollar amounts and find the qty integers
      let qty = 1;
      const descStripped = description.replace(/\$[\d,]+\.?\d*/g, '');
      const qtyMatch = descStripped.match(/(\d+)\s+\1\s*$/); // "3 3" at end = qty ordered + delivered
      if (qtyMatch) {
        qty = parseInt(qtyMatch[1]);
        description = descStripped.slice(0, qtyMatch.index).trim();
      } else {
        // Try plain number before the prices
        const plainBeforePrice = descStripped.match(/\s(\d{1,4})\s*$/);
        if (plainBeforePrice) {
          qty = parseInt(plainBeforePrice[1]);
          description = descStripped.slice(0, plainBeforePrice.index).trim();
        }
      }

      // Clean row number from start of description
      description = description.replace(/^\d+\s+/, '').trim();
      // Remove trailing comment-column words that got appended (single words like "Bar")
      // We don't strip them — we append them per user request

      pending = { qty, description, unitPrice, totalAmount };

    } else if (pending && amounts.length === 0) {
      // Continuation of previous description
      pending.description += ' ' + line.trim();
    }
  }

  if (pending) items.push(finalizeAureus(pending));
  return items.filter(i => i.unitPrice > 0 && i.description.length > 2);
}

function finalizeAureus(item) {
  const desc = cleanDescription(item.description);
  return {
    description: desc,
    qty:         item.qty || 1,
    unitPrice:   item.unitPrice,
    totalAmount: item.totalAmount || item.unitPrice * (item.qty || 1),
    metal:       detectMetal(desc),
    category:    detectCategory(desc),
    weight:      detectWeight(desc),
  };
}

// ══════════════════════════════════════════════════════════════════════════
// AGC BUY INVOICE PARSER (original format: QTY | ITEM | PRICE | TOTAL)
// ══════════════════════════════════════════════════════════════════════════
function parseAGCBuyInvoice(lines, result) {
  // ── Invoice number ─────────────────────────────────────────────────────
  for (const line of lines) {
    const m = line.match(/invoice\s*#?:?\s*([A-Z0-9\-]{2,20})/i);
    if (m) { result.invoiceNumber = m[1].trim(); break; }
  }
  if (!result.invoiceNumber) {
    for (const line of lines) {
      const m = line.match(/#:?\s*(\d{3,10})/);
      if (m) { result.invoiceNumber = m[1]; break; }
    }
  }

  // ── Client / Vendor ─────────────────────────────────────────────────────
  for (const line of lines) {
    const m = line.match(/client:?\s+(.+)/i);
    if (m) { result.vendor = m[1].trim(); break; }
  }
  if (!result.vendor) {
    for (const line of lines.slice(0, 10)) {
      if (/client|contact|invoice|date|#|\d{3}-\d{4}|@|www\.|suite|ste\.|rd\.|blvd/i.test(line)) continue;
      if (line.length > 3 && line.length < 50 && /^[A-Z]/.test(line)) {
        result.vendor = line; break;
      }
    }
  }

  // ── Date ───────────────────────────────────────────────────────────────
  for (const line of lines) {
    const m = line.match(/date:?\s*(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})/i);
    if (m) {
      const parsed = new Date(m[1]);
      if (!isNaN(parsed)) { result.date = parsed.toISOString().split('T')[0]; break; }
    }
  }
  if (!result.date) {
    for (const line of lines) {
      const m = line.match(/(\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4})/);
      if (m) {
        const parsed = new Date(m[1]);
        if (!isNaN(parsed) && parsed.getFullYear() > 2000) {
          result.date = parsed.toISOString().split('T')[0]; break;
        }
      }
    }
  }

  // ── Line items ─────────────────────────────────────────────────────────
  // AGC invoice format: QTY   Item Description   PRICE   TOTAL
  // Strategy: find lines where we can extract a price and a leading integer qty.

  // First, find the header row to know we're in the line item section
  let inItems   = false;
  let itemBuffer = ''; // for multi-line descriptions

  // Collect logical rows by joining continuation lines
  const logicalLines = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    // Header row detection
    if (/^\s*qty\s+item\s+price\s+total/i.test(line)) { inItems = true; continue; }
    if (/^\s*total\s*\$?[\d,]+\.\d{2}\s*$/i.test(line)) { inItems = false; continue; }
    if (!inItems) continue;
    // Skip pure header/footer lines
    if (/^(qty|item|price|total|subtotal|tax|shipping|page\s*\d)/i.test(line)) continue;
    logicalLines.push(line);
  }

  // If we found structured lines, parse them
  if (logicalLines.length > 0) {
    result.lineItems = parseStructuredLines(logicalLines);
  }

  // Fallback: scan all lines for price-at-end patterns
  if (result.lineItems.length === 0) {
    result.lineItems = parseFallback(lines);
  }

  return result;
}

// ── Structured parser (AGC format: "12  4pc Gold Eagle Proof Set  $8,852.04  $106,224.42") ──
function parseStructuredLines(lines) {
  const items     = [];
  let pending     = null; // buffering multi-line descriptions

  // Amount pattern: $ with optional comma-formatted number
  const amtPat    = /\$?\s*([\d]{1,3}(?:,\d{3})*(?:\.\d{2})?)/g;

  for (const line of lines) {
    // Does this line start with an integer quantity?
    const qtyMatch = line.match(/^(\d{1,4})\s+(.+)/);

    // Find all dollar/price amounts on this line
    const amounts  = [];
    let m;
    const pat = /\$\s*([\d]{1,3}(?:,\d{3})*\.\d{2})/g;
    while ((m = pat.exec(line)) !== null) {
      amounts.push(parseFloat(m[1].replace(/,/g, '')));
    }

    if (qtyMatch && amounts.length >= 1) {
      // Flush any pending buffered item
      if (pending) { items.push(finalize(pending)); pending = null; }

      const qty         = parseInt(qtyMatch[1]);
      // Description is everything between qty and the first price
      let description   = qtyMatch[2].replace(/\$[\d,]+\.\d{2}/g, '').trim();
      // The last amount is usually TOTAL, second-to-last is PRICE
      const total       = amounts[amounts.length - 1];
      const unitPrice   = amounts.length >= 2 ? amounts[amounts.length - 2] : total / qty;

      pending = { qty, description, unitPrice, totalAmount: total };

    } else if (pending && amounts.length === 0) {
      // Continuation line for the previous description (no prices = more description text)
      pending.description += ' ' + line.trim();

    } else if (pending && amounts.length >= 1) {
      // Continuation line that happens to have a price (e.g. "Copper Spot $3,579.14 $3,579.14")
      // Append text part to description, use amounts as new prices
      const textPart    = line.replace(/\$[\d,]+\.\d{2}/g, '').trim();
      if (textPart) pending.description += ' ' + textPart;
      const total       = amounts[amounts.length - 1];
      const unitPrice   = amounts.length >= 2 ? amounts[amounts.length - 2] : pending.unitPrice;
      pending.unitPrice    = unitPrice;
      pending.totalAmount  = total;
      // If previous pending had a real qty*price match, flush it
      items.push(finalize(pending));
      pending = null;

    } else if (!pending && amounts.length >= 2) {
      // Line with prices but no leading qty — could be a continuation with prices
      // Skip
    }
  }
  if (pending) items.push(finalize(pending));
  return items.filter(i => i.unitPrice > 0 && i.description.length > 2);
}

function finalize(item) {
  const desc = cleanDescription(item.description);
  return {
    description: desc,
    qty:         item.qty || 1,
    unitPrice:   item.unitPrice,
    totalAmount: item.totalAmount || item.unitPrice * item.qty,
    metal:       detectMetal(desc),
    category:    detectCategory(desc),
    weight:      detectWeight(desc),
  };
}

// ── Fallback parser (no header structure found) ────────────────────────────
function parseFallback(lines) {
  const items = [];
  for (const line of lines) {
    if (/total|subtotal|tax|shipping|thank|payment|balance|remit|page|@|www\./i.test(line)) continue;
    const amounts = [];
    const pat = /\$\s*([\d]{1,3}(?:,\d{3})*\.\d{2})/g;
    let m;
    while ((m = pat.exec(line)) !== null) amounts.push(parseFloat(m[1].replace(/,/g,'')));
    if (amounts.length === 0) continue;

    const unitPrice = amounts.length >= 2 ? amounts[amounts.length - 2] : amounts[amounts.length - 1];
    const total     = amounts[amounts.length - 1];
    if (unitPrice <= 0) continue;

    const qtyMatch   = line.match(/^(\d{1,4})\s+/);
    const qty        = qtyMatch ? parseInt(qtyMatch[1]) : 1;
    let description  = line.replace(/\$[\d,]+\.\d{2}/g, '').replace(/^\d+\s+/, '').trim();
    if (description.length < 3) continue;

    items.push({
      description: cleanDescription(description),
      qty,
      unitPrice,
      totalAmount: total,
      metal:    detectMetal(description),
      category: detectCategory(description),
      weight:   detectWeight(description),
    });
  }
  return items;
}

// ── METAL DETECTION ─────────────────────────────────────────────────────────
function detectMetal(text) {
  const t = text.toUpperCase();
  // Palladium first (before platinum, which also starts with 'P')
  if (/PALLADIUM|PALLAD/.test(t))                                          return 'Palladium';
  if (/PLATINUM|PLAT\b|PT\b|PREAMBLE.*PLAT|PLAT.*EAGLE/.test(t))          return 'Platinum';
  if (/\bGOLD\b|AU\b|BUFFALO|EAGLE.*GOLD|GOLD.*EAGLE|MERCURY.*GOLD|GOLD.*MERCURY|GOLD.*WALK|GOLD.*STAND|LIBERTY.*GOLD|GOLD.*LIBERT|ROSE\s*GOLD|GOLD\s*\$5|GOLD\s*COIN|KENNEDY.*GOLD|FIRST\s*SPOUSE/.test(t)) return 'Gold';
  if (/SILVER|AG\b|ASE|MORGAN|PEACE|WALKER|FRANKLIN|KENNEDY|MERC\b|MERCURY\b|DIME\b|QUARTER\b(?!.*GOLD)|HALF\b(?!.*GOLD)|PROOF\s*SET|SILVER\s*EAGLE|SILVER\s*DOLLAR/.test(t)) return 'Silver';
  // Coin names that are silver by default
  if (/GUILDER|GUILDER|PRESTIGE|PROOF\s*SET/.test(t))                      return 'Silver';
  return 'Gold'; // default for unknown gold-sounding items
}

// ── CATEGORY DETECTION ──────────────────────────────────────────────────────
function detectCategory(text) {
  const t     = text.toUpperCase();
  const metal = detectMetal(text);

  if (/BAR\b/.test(t))                                                      return `${metal} Bar`;
  if (/JEWELRY|RING|BRACELET|NECKLACE|CHAIN|EARRING|PENDANT/.test(t))       return `${metal} Jewelry`;
  if (/SCRAP|MELT|JUNK|90%|40%/.test(t))                                    return 'Scrap Metal';
  if (/BILL|NOTE|CURRENCY|PAPER\s*MONEY/.test(t))                           return 'Currency / Paper';

  // Multi-coin sets, proof sets, medal sets
  if (/SET|SETS|MEDAL\s*SET|PROOF\s*SET|EDITION\s*PROOF|COMMEMORATIVE\s*\d/.test(t)) return 'Numismatic/Collectible';

  // Commemoratives, special issues
  if (/COMMEM|ANNIVERSARY|HIGH\s*RELIEF|PRESID|PREAMBLE|APOLLO|BREAST|WWI|PRIDE|ATB|QUARTERS/.test(t)) return 'Numismatic/Collectible';

  // GSA, graded, slabbed
  if (/GSA|PCGS|NGC|ANACS|MS\d{2}|PR\d{2}/.test(t))                        return 'Numismatic/Collectible';

  // Named type coins — silver
  if (/MORGAN|PEACE|KENNEDY\s*HALF|WALKING|BARBER|SEATED|TRADE|GUILDER/.test(t)) return 'Silver Coin';

  // Generic coins by metal
  if (/COIN|EAGLE|MAPLE|KRUGER|BUFFALO|SOVEREIGN|PANDA|LIBERTAD|PHILHARMONIC/.test(t)) return `${metal} Coin`;

  return `${metal} Coin`;
}

// ── WEIGHT DETECTION ────────────────────────────────────────────────────────
function detectWeight(text) {
  const t = text.toUpperCase();

  // Explicit oz fractions in description
  if (/10\s*OZ/.test(t)) return 10;
  if (/5\s*OZ/.test(t))  return 5;
  if (/4\s*OZ/.test(t))  return 4;
  if (/3\s*OZ/.test(t))  return 3;
  if (/2\s*OZ/.test(t))  return 2;
  if (/1\s*OZ/.test(t))  return 1;
  if (/3\/4\s*OZ|0\.75\s*OZ/.test(t)) return 0.75;
  if (/1\/2\s*OZ|0\.5\s*OZ/.test(t))  return 0.5;
  if (/1\/4\s*OZ|0\.25\s*OZ/.test(t)) return 0.25;
  if (/1\/10\s*OZ|0\.1\s*OZ/.test(t)) return 0.1;

  // Named coin weights (approximate troy oz)
  if (/SILVER\s*DOLLAR|MORGAN|PEACE|WALKING.*HALF|PRESIDENT.*MEDAL/.test(t)) return 0.7734;
  if (/KENNEDY.*HALF|HALF.*DOLLAR/.test(t)) return 0.3617;  // pre-65: 0.36; 40%: 0.148
  if (/QUARTER(?!\s*SET)/.test(t)) return 0.1808;
  if (/DIME/.test(t))  return 0.0723;
  if (/GUILDER/.test(t)) return 0.578; // .720 10G = .578 ASW

  // ATB 5oz quarters
  if (/ATB/.test(t)) return 5;

  // Commemorative gold $5 = ~0.2418 oz
  if (/GOLD\s*\$5|\$5.*GOLD/.test(t)) return 0.2418;

  // Gold $10 = ~0.4837 oz
  if (/GOLD\s*\$10|\$10.*GOLD/.test(t)) return 0.4837;

  return 0;
}

// ── Cleanup ──────────────────────────────────────────────────────────────────
function cleanDescription(desc) {
  return desc
    .replace(/\s{2,}/g, ' ')
    .replace(/^[-•*|]+/, '')
    .trim();
}
